/**
 * Payroll Adjustment Form — Task 8
 * =================================
 *
 * Deterministic payroll add-back editor. Every entry lands in
 * drafts.data.accountant_adjustments.payroll[] and feeds
 * BottomUpCalculator.calculate(adjustments=...) — the engine subtracts
 * each in-loss-period adjustment from continuing expenses.
 *
 * Replaces the old "tell the forensic agent in chat" workflow that
 * defeated the LLMs-interpret-code-calculates rule.
 *
 * Defaults (user-confirmed):
 *   1. Period format: month-level YYYY-MM
 *   2. Retroactive: POST invalidates forensic_analyses cache; next
 *      read re-runs with fresh adjustments. No auto-trigger here.
 *   3. Revert: DELETE filters out the row. Audit chain records both
 *      the creation AND the deletion for the full paper trail.
 *   4. Category taxonomy: officer_comp | non_continuing_wages |
 *      bonus_timing | other.
 *   5. Mount: tab inside AccountantReconciliation (alongside Merchant
 *      Review).
 *   6. Amount constraints: any non-negative integer cents. No ceiling.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  Plus,
  Trash2,
  AlertCircle,
} from 'lucide-react';

import api from '../../utils/api';
import { FinancialValue } from '../shared/FinancialValue';

// ---------------------------------------------------------------------------
// Constants + types
// ---------------------------------------------------------------------------

const CATEGORIES = [
  { value: 'officer_comp', label: 'Officer compensation' },
  { value: 'non_continuing_wages', label: 'Non-continuing wages' },
  { value: 'bonus_timing', label: 'Bonus timing' },
  { value: 'other', label: 'Other' },
] as const;

type Category = typeof CATEGORIES[number]['value'];

interface PayrollAdjustment {
  adjustment_id: string;
  category: Category;
  amount_cents: number;
  period: string;
  reason: string;
  author_id: string;
  author_name: string;
  org_id: string;
  created_at: string;
}

interface ListResponse {
  report_id: string;
  adjustments: PayrollAdjustment[];
  total_cents: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface Props {
  reportId: string;
}

export default function PayrollAdjustmentForm({ reportId }: Props) {
  const [data, setData] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [category, setCategory] = useState<Category>('officer_comp');
  const [period, setPeriod] = useState('');
  const [amountDollars, setAmountDollars] = useState('');
  const [reason, setReason] = useState('');

  async function reload() {
    setLoading(true);
    setLoadError(null);
    try {
      const resp = await api.get<ListResponse>(
        `/api/v1/adjustments/payroll/${reportId}`,
      );
      setData(resp);
    } catch (err: any) {
      setLoadError(err?.message || 'Failed to load adjustments.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    const dollars = parseFloat(amountDollars);
    if (!(dollars >= 0)) {
      setFormError('Amount must be a non-negative number.');
      return;
    }
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(period)) {
      setFormError('Period must be in YYYY-MM format.');
      return;
    }
    if (!reason.trim()) {
      setFormError('Reason is required for the audit trail.');
      return;
    }

    const amount_cents = Math.round(dollars * 100);

    setSubmitting(true);
    try {
      await api.post(`/api/v1/adjustments/payroll/${reportId}`, {
        category,
        amount_cents,
        period,
        reason: reason.trim(),
      });
      setCategory('officer_comp');
      setPeriod('');
      setAmountDollars('');
      setReason('');
      await reload();
    } catch (err: any) {
      setFormError(err?.message || 'Save failed.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(id: string) {
    try {
      await api.delete(`/api/v1/adjustments/payroll/${reportId}/${id}`);
      await reload();
    } catch (err: any) {
      setFormError(err?.message || 'Delete failed.');
    }
  }

  const runningTotalDollars = useMemo(() => {
    return ((data?.total_cents ?? 0) / 100).toFixed(2);
  }, [data?.total_cents]);

  if (loading && !data) {
    return (
      <div className="p-6 flex items-center gap-2" style={{ color: 'var(--color-text-muted)' }}>
        <Loader2 size={16} className="animate-spin" />
        Loading adjustments…
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        role="alert"
        className="m-6 rounded-lg border p-4"
        style={{ borderColor: 'var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
      >
        <AlertCircle className="inline mr-2" size={18} />
        {loadError}
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="payroll-adjustment-form">
      {/* Header + intro */}
      <div>
        <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
          Payroll Add-Backs
        </h2>
        <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>
          Deterministic adjustments that reduce continuing payroll
          expense in the bottom-up ISO CP 00 30 calculation. Entered
          here, not in chat — every dollar is audited.
        </p>
      </div>

      {/* Entry form */}
      <form
        onSubmit={handleSubmit}
        className="border rounded-lg p-4 grid grid-cols-1 md:grid-cols-4 gap-3"
        style={{ borderColor: 'var(--color-border)' }}
      >
        <label className="block">
          <span className="block text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>Category</span>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as Category)}
            data-testid="input-category"
            className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none"
            style={{ borderColor: 'var(--color-border)' }}
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block">
          <span className="block text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>Period</span>
          <input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            required
            data-testid="input-period"
            className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none"
            style={{ borderColor: 'var(--color-border)' }}
          />
        </label>

        <label className="block">
          <span className="block text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>Amount ($)</span>
          <input
            type="number"
            step="0.01"
            min="0"
            value={amountDollars}
            onChange={(e) => setAmountDollars(e.target.value)}
            required
            data-testid="input-amount"
            className="w-full border rounded px-2 py-1.5 text-sm text-right font-mono focus:outline-none"
            style={{ borderColor: 'var(--color-border)' }}
          />
        </label>

        <label className="block md:col-span-1">
          <span className="block text-xs mb-1" style={{ color: 'var(--color-text-secondary)' }}>Reason</span>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            data-testid="input-reason"
            placeholder="Audit-trail note"
            className="w-full border rounded px-2 py-1.5 text-sm focus:outline-none"
            style={{ borderColor: 'var(--color-border)' }}
          />
        </label>

        <div className="md:col-span-4 flex items-center justify-between">
          <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            Stored in cents · invalidates forensic cache on save
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="submit-adjustment"
            className="flex items-center gap-2 px-4 py-1.5 text-sm font-medium rounded disabled:cursor-not-allowed"
            style={{ backgroundColor: submitting ? 'var(--color-text-ghost)' : 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            {submitting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Plus size={14} />
            )}
            Add adjustment
          </button>
        </div>

        {formError && (
          <div
            role="alert"
            data-testid="form-error"
            className="md:col-span-4 rounded border p-2 text-xs"
            style={{ borderColor: 'var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
          >
            {formError}
          </div>
        )}
      </form>

      {/* List of existing adjustments */}
      <div className="border rounded-lg overflow-hidden" style={{ borderColor: 'var(--color-border)' }}>
        <div className="px-4 py-2 flex items-center justify-between" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
            Adjustments on file
          </h3>
          <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            Running total:{' '}
            <span className="font-mono">${runningTotalDollars}</span>
          </span>
        </div>

        {data && data.adjustments.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs border-b" style={{ color: 'var(--color-text-muted)', borderColor: 'var(--color-border)' }}>
                <th className="px-4 py-2 font-medium">Period</th>
                <th className="px-4 py-2 font-medium">Category</th>
                <th className="px-4 py-2 font-medium text-right">Amount</th>
                <th className="px-4 py-2 font-medium">Reason</th>
                <th className="px-4 py-2 font-medium">Entered by</th>
                <th className="px-4 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.adjustments.map((a) => (
                <tr
                  key={a.adjustment_id}
                  className="border-b last:border-b-0"
                  style={{ borderColor: 'var(--color-border-faint)' }}
                  data-testid={`adjustment-row-${a.adjustment_id}`}
                >
                  <td className="px-4 py-2 font-mono text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    {a.period}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {CATEGORIES.find((c) => c.value === a.category)?.label ||
                      a.category}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <FinancialValue value={a.amount_cents / 100} size="sm" />
                  </td>
                  <td className="px-4 py-2 text-xs italic max-w-[32ch] truncate" style={{ color: 'var(--color-text-secondary)' }}>
                    {a.reason}
                  </td>
                  <td className="px-4 py-2 text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    {a.author_name}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      onClick={() => handleDelete(a.adjustment_id)}
                      className="p-1"
                      style={{ color: 'var(--color-text-dim)' }}
                      title="Delete adjustment"
                      data-testid={`delete-${a.adjustment_id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="px-4 py-6 text-sm" style={{ color: 'var(--color-text-dim)' }}>
            No adjustments entered yet.
          </p>
        )}
      </div>
    </div>
  );
}
